import type { AxiosInstance } from 'axios';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ReelViewModel } from '../types/reel';
import type { ReelFeedResult } from '../types/reel/ReelFeedResult';
import { apiClient } from '../client/client';

export interface ReelFeedParams {
  take: number;
  cursorUtc?: string | null;
  excludeIds?: string[] | null;
}

/**
 * Reels API: feed (cursor-based), create (multipart), delete.
 */
export class ReelService {
  private static readonly BASE = '/api/reels';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  /**
   * GET /api/reels/feed with take, cursorUtc, excludeIds.
   * excludeIds sent as repeated query params: excludeIds=id1&excludeIds=id2
   */
  async getFeed(params: ReelFeedParams): Promise<ServiceResultT<ReelFeedResult>> {
    const searchParams = new URLSearchParams();
    searchParams.set('take', String(params.take));
    if (params.cursorUtc != null && params.cursorUtc !== '') {
      searchParams.set('cursorUtc', params.cursorUtc);
    }
    if (params.excludeIds != null) {
      params.excludeIds.forEach((id) => searchParams.append('excludeIds', id));
    }
    const queryString = searchParams.toString();
    const url = queryString
      ? `${ReelService.BASE}/feed?${queryString}`
      : `${ReelService.BASE}/feed`;
    const { data } = await this.client.get<ServiceResultT<ReelFeedResult>>(url);
    return data;
  }

  /**
   * POST /api/reels with multipart/form-data (video file).
   */
  async create(formData: FormData): Promise<ServiceResultT<ReelViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ReelViewModel>>(
      ReelService.BASE,
      formData
    );
    return data;
  }

  /**
   * DELETE /api/reels/{id}.
   */
  async delete(id: string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(
      `${ReelService.BASE}/${id}`
    );
    return data;
  }
}

export const reelService = new ReelService();
